import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { HTTPError, post } from "common/helpers/HTTP";
import type { ConversationParticipant } from "common/types";

import { generateAudioFilename } from "../utils";
import useUpdateConversation from "./useUpdateConversation";

import type { InboxMessageSchemaType } from "../schemas";
import type { ConversationMessage } from "../types";

async function sendMessageRequest(
  workspaceId: string,
  accountId: string,
  recipients: ConversationParticipant[],
  data: InboxMessageSchemaType,
  conversationUrn?: string,
) {
  let basePath = `workspaces/${workspaceId}/accounts/${accountId}/conversations`;

  if (conversationUrn) {
    basePath += `/${conversationUrn}`;
  }

  let requestBody: Record<string, unknown> | FormData;

  if ("audio" in data) {
    const { audio } = data;

    requestBody = new FormData();
    requestBody.append("file", audio);
    requestBody.append("file_name", generateAudioFilename(audio.type));
    recipients.forEach((recipient) => {
      (requestBody as FormData).append("recipients", recipient.id);
    });

    basePath += "/send-audio";
  } else {
    const { attachments } = data;

    requestBody = {
      message: "message" in data ? data.message.trim() : "",
      recipients: recipients.map(({ id }) => id),
      file_identifiers: attachments?.map((file) => file.id),
    };
  }

  const {
    conversation_urn: returnedConversationUrn,
    message_urn: messageUrn,
    created_at: createdAt,
  } = await post(basePath, requestBody);

  return {
    createdAt,
    recipients,
    conversationUrn: returnedConversationUrn,
    messageUrn,
  };
}
async function markAsReadRequest(
  workspaceId: string,
  accountId: string,
  conversationUrn: string,
) {
  await post(
    `workspaces/${workspaceId}/accounts/${accountId}/conversations/${conversationUrn}/read`,
  );
}
async function markAsUnreadRequest(
  workspaceId: string,
  accountId: string,
  conversationUrn: string,
): Promise<number> {
  const { unread_count } = await post(
    `workspaces/${workspaceId}/accounts/${accountId}/conversations/${conversationUrn}/unread`,
  );
  return unread_count;
}

export default function useConversationUpdates() {
  const queryClient = useQueryClient();
  const { id: workspaceId, accounts } = useSelectedWorkspaceContext();
  const { updateConversation, addMessage } = useUpdateConversation();
  const { leadsKeys, conversationsKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: sendMessage, isPending: isSendingMessage } = useMutation(
    {
      mutationFn: async ({
        accountId,
        recipients,
        data,
        conversationUrn,
      }: {
        accountId: string;
        recipients: ConversationParticipant[];
        data: InboxMessageSchemaType;
        conversationUrn?: string;
      }) =>
        sendMessageRequest(
          workspaceId,
          accountId,
          recipients,
          data,
          conversationUrn,
        ),
      onSuccess: async (
        {
          createdAt,
          recipients,
          conversationUrn: returnedConversationUrn,
          messageUrn,
        },
        { accountId, data },
      ) => {
        await queryClient.cancelQueries({
          queryKey: conversationsKeys.messages(
            returnedConversationUrn,
            accountId,
          ),
        });

        const senderAccount = accounts?.find((acc) => acc.id === accountId);

        const baseMessageProperties = {
          urn: messageUrn,
          created_at: createdAt,
          deleted: false,
          edited: false,
          sender: {
            id: accountId,
            full_name: senderAccount?.full_name,
            picture_url: senderAccount?.picture_url,
          },
          inmail: false,
          declined: false,
          pending: true,
        };

        let newMessage: ConversationMessage;

        if ("audio" in data) {
          newMessage = {
            ...baseMessageProperties,
            body: "", // audio doesn't have text body, it's empty string,
            audio: undefined,
          };
        } else {
          const { attachments } = data;
          newMessage = {
            ...baseMessageProperties,
            body: "message" in data ? data.message : "",
            attachments,
          };
        }

        updateConversation(
          returnedConversationUrn,
          (draftConversation) => {
            draftConversation.last_message = newMessage;
            // this is for conversations that are new in the cache
            if (!draftConversation.participants) {
              draftConversation.participants = recipients;
            }
            if (!draftConversation.last_activity_at) {
              draftConversation.last_activity_at = createdAt;
            }
          },
          accountId,
        );
        addMessage(returnedConversationUrn, accountId, newMessage);

        // for conversations with leads that don't have a conversationUrn yet
        if (
          recipients.length === 1 &&
          !queryClient.getQueryData(leadsKeys.conversationUrn(recipients[0].id))
        ) {
          queryClient.setQueryData(
            leadsKeys.conversationUrn(recipients[0].id),
            returnedConversationUrn,
          );
        }
      },
      onError: (error) => {
        if (error instanceof HTTPError) {
          const errorMessages: Record<string, string> = {
            RecipientNotConnection:
              "You can only send messages to your first degree connections",
            BlockedByRecipient:
              "Cannot send message because you have been blocked by the recipient",
          };

          const message = errorMessages[error.data];
          if (message) {
            toast.error(message);
          } else {
            toast.error("Unable to send a message, please try again later");
          }
        }
      },
    },
  );

  const { mutateAsync: markAsRead } = useMutation({
    mutationFn: ({
      accountId,
      conversationUrn,
    }: {
      accountId: string;
      conversationUrn: string;
    }) => markAsReadRequest(workspaceId, accountId, conversationUrn),
    onSuccess: (_, { conversationUrn }) => {
      updateConversation(conversationUrn, (draftConversation) => {
        draftConversation.unread_count = 0;
      });
    },
  });

  const { mutateAsync: markAsUnread } = useMutation({
    mutationFn: ({
      accountId,
      conversationUrn,
    }: {
      accountId: string;
      conversationUrn: string;
    }) => markAsUnreadRequest(workspaceId, accountId, conversationUrn),
    onSuccess: (unreadCount, { conversationUrn }) => {
      updateConversation(conversationUrn, (draftConversation) => {
        draftConversation.unread_count = unreadCount;
      });
    },
    onError: () => {
      toast.error("Unable to mark as unread, please try again later");
    },
  });

  return { sendMessage, isSendingMessage, markAsRead, markAsUnread };
}
